import React, { useCallback, useState, useRef } from 'react';
import { Container, Box, CircularProgress, Typography } from '@mui/material';
// DEMO: Use CostCalculatorDemo instead of CostCalculator
import CostCalculatorDemo from '../components/CostCalculatorDemo';
import AppToolbar from '../components/AppToolbar';
import GlobalSettingsDialog from '../components/calculator/modals/GlobalSettingsDialog';
import FMCRegionWarningDialog from '../components/calculator/modals/FMCRegionWarningDialog';
import { CalculatorProvider } from '../contexts/CalculatorContext';
import { useTabs } from '../contexts/TabsContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

export default function CalculatorPageDemo() {
  const { activeTab, updateActiveTabData, loading, error } = useTabs();
  const {
    language,
    region,
    setLanguage,
    setRegion,
    loading: prefsLoading
  } = useUserPreferences();

  // FMC region warning dialog state
  const [fmcWarningOpen, setFmcWarningOpen] = useState(false);
  const pendingRegionRef = useRef(null);

  const handleStateSave = useCallback((newState) => {
    updateActiveTabData(newState);
  }, [updateActiveTabData]);

  const handleRegionChangeRequest = useCallback((currentRegion, newRegion) => {
    const isFromUkraine = currentRegion === 'ua' ||
                         String(currentRegion).toLowerCase().includes('ukr') ||
                         String(currentRegion).toLowerCase().includes('ua') ||
                         String(currentRegion).toLowerCase() === 'ukraine';

    const isToNonUkraine = !(newRegion === 'ua' ||
                            String(newRegion).toLowerCase().includes('ukr') ||
                            String(newRegion).toLowerCase().includes('ua') ||
                            String(newRegion).toLowerCase() === 'ukraine');

    const tabData = activeTab?.settings || {};
    const businessPhoneType = tabData.businessPhoneType || 'business-phone';
    const fmcUsers = tabData.fmcUsers || 0;
    const hasFMC = (businessPhoneType === 'fmc' || businessPhoneType === 'business-phone-fmc') && fmcUsers > 0;

    if (isFromUkraine && isToNonUkraine && hasFMC) {
      pendingRegionRef.current = newRegion;
      setFmcWarningOpen(true);
    } else {
      setRegion(newRegion);
    }
  }, [activeTab, setRegion]);

  const handleFmcWarningConfirm = useCallback(() => {
    setFmcWarningOpen(false);
    if (pendingRegionRef.current) {
      const currentTabData = activeTab?.settings || {};
      const updatedSettings = {
        ...currentTabData,
        businessPhoneType: 'business-phone',
        fmcUsers: 0
      };
      updateActiveTabData(updatedSettings);
      setRegion(pendingRegionRef.current);
      pendingRegionRef.current = null;
    }
  }, [setRegion, activeTab, updateActiveTabData]);

  const handleFmcWarningCancel = useCallback(() => {
    setFmcWarningOpen(false);
    pendingRegionRef.current = null;
  }, []);

  const renderContent = () => {
    if (loading || prefsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
          <Typography color="error">Failed to load tabs: {error.message}</Typography>
        </Box>
      );
    }

    if (!activeTab) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
          <Typography>No active tabs. Create a new one to start.</Typography>
        </Box>
      );
    }

    return (
      <CalculatorProvider
        key={activeTab.id}
        instanceTabId={activeTab.id}
        tabData={activeTab.settings || {}}
        onStateSave={handleStateSave}
      >
        <Container maxWidth="xl" sx={{ pt: 4, pb: 4 }}>
          {/* DEMO: Using CostCalculatorDemo instead of CostCalculator */}
          <CostCalculatorDemo />
        </Container>
        <GlobalSettingsDialog open={false} onClose={() => {}} />
      </CalculatorProvider>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7fafb' }}>
      <AppToolbar
        region={region}
        setRegion={setRegion}
        language={language}
        setLanguage={setLanguage}
        hasGlobalSettings={false}
        onOpenGlobalSettings={() => {}}
        onRegionChangeRequest={handleRegionChangeRequest}
      />
      {renderContent()}
      <FMCRegionWarningDialog
        open={fmcWarningOpen}
        onConfirm={handleFmcWarningConfirm}
        onCancel={handleFmcWarningCancel}
      />
    </Box>
  );
}
